import { createFileRoute, redirect } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { api, ApiError } from "../../lib/api";
import type { CurrentUser } from "../../lib/auth";
import { DialogPanel } from "../../components/shared/DialogPanel";
import { AlertTriangleIcon, XIcon } from "../../components/shared/Icons";
import { backdropClose } from "../../components/shared/backdrop";
import { toastError } from "../../components/shared/toast";

// ─── Tipos (espelham routers/finance.py) ────────────────────────────────────
interface Values { lead_limit: number | null; extra_price: number | null }
interface Row {
  agent_id: string; name: string; status: string; manager_name: string | null;
  leads_unicos: number; leads_ia: number; lead_limit: number | null; extra_price: number | null;
  extra_leads: number; extra_cost: number; ajustado: boolean; default: Values; month_override: Values;
  leads_nao_ia: number; ia_por_canal: Counts; nao_por_canal: Counts; nao_por_motivo: Counts;
}
type Counts = Record<string, number>;
type SortKey = "name" | "leads_unicos" | "leads_ia" | "leads_nao_ia" | "lead_limit" | "extra_price" | "extra_leads" | "extra_cost";

export const Route = createFileRoute("/_authed/financeiro")({
  beforeLoad: ({ context }) => {
    const u = (context as { user?: CurrentUser }).user;
    if (u && u.role_name !== "Admin" && !u.permissions?.financeiro) throw redirect({ to: "/" });
  },
  component: FinanceiroPage,
});

// ─── Estilos (padrão patioo.tsx / roles.tsx) ────────────────────────────────
const btnPrimary: React.CSSProperties = { background: "var(--brand)", color: "#fff", padding: "7px 14px", borderRadius: 8, fontSize: 13, fontWeight: 500, border: "none", cursor: "pointer" };
const btnGhost: React.CSSProperties = { background: "none", color: "var(--ink-secondary)", padding: "7px 14px", borderRadius: 8, fontSize: 13, border: "1px solid var(--line-strong)", cursor: "pointer" };
const inputStyle: React.CSSProperties = { width: "100%", padding: "8px 10px", background: "var(--surface-input)", border: "1px solid var(--line)", borderRadius: 8, color: "var(--ink)", fontSize: 13, outline: "none", boxSizing: "border-box" };
const labelStyle: React.CSSProperties = { display: "block", fontSize: 12, fontWeight: 600, color: "var(--ink-secondary)", marginBottom: 6 };
const card: React.CSSProperties = { background: "var(--surface-card)", border: "1px solid var(--line)", borderRadius: 12, padding: 16 };
const thBase: React.CSSProperties = { padding: "10px 14px", fontSize: 11, fontWeight: 600, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em", borderBottom: "1px solid var(--line)", whiteSpace: "nowrap", position: "sticky", top: 0, background: "var(--surface-card)", zIndex: 1 };
const tdStyle: React.CSSProperties = { padding: "11px 14px", fontSize: 13, color: "var(--ink)", borderBottom: "1px solid var(--line)", whiteSpace: "nowrap" };
const numCell: React.CSSProperties = { ...tdStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" };

const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const int = new Intl.NumberFormat("pt-BR");
const thisMonth = () => new Date().toLocaleDateString("sv-SE", { timeZone: "America/Sao_Paulo" }).slice(0, 7);
const shiftMonth = (m: string, d: number) => {
  const [y, mo] = m.split("-").map(Number);
  const t = new Date(y, mo - 1 + d, 1);
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}`;
};
const monthLabel = (m: string) => {
  const [y, mo] = m.split("-").map(Number);
  return new Date(y, mo - 1, 1).toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
};
const errMsg = (e: unknown) => (e instanceof ApiError && (e.payload as { detail?: string } | undefined)?.detail) || "Algo deu errado.";
const STATUS_LABEL: Record<string, string> = { inactive: "inativo", development: "em desenvolvimento" };

const CANAL_LABEL: Record<string, string> = { waba: "WhatsApp", whatsapp: "WhatsApp", instagram: "Instagram", messenger: "Messenger", portal: "Portal", outro: "Outro" };
const sumCounts = (list: Counts[]) => list.reduce<Counts>((acc, d) => {
  for (const [k, v] of Object.entries(d)) acc[k] = (acc[k] ?? 0) + v;
  return acc;
}, {});

// Popup de quebra (canal / motivo). Some quando não há nada pra mostrar.
function splitContent(parts: { title: string; data: Counts; canal?: boolean }[]) {
  const shown = parts.filter(p => Object.keys(p.data).length);
  if (!shown.length) return null;
  return (
    <div style={{ display: "flex", gap: 20 }}>
      {shown.map(p => (
        <div key={p.title} style={{ minWidth: 150 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 6 }}>{p.title}</div>
          {Object.entries(p.data).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 16, fontSize: 13, padding: "2px 0", color: "var(--ink)" }}>
              <span>{p.canal ? (CANAL_LABEL[k] ?? k) : k}</span>
              <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{int.format(v)}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// position: fixed — escapa do overflow da tabela; vira pra cima perto do rodapé.
function Hover({ content, children }: { content: React.ReactNode; children: React.ReactNode }) {
  const [pos, setPos] = useState<{ top?: number; bottom?: number; right: number } | null>(null);
  if (!content) return <>{children}</>;
  const show = (e: React.SyntheticEvent<HTMLElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const right = Math.max(8, window.innerWidth - r.right);
    setPos(r.bottom + 220 > window.innerHeight ? { bottom: window.innerHeight - r.top + 6, right } : { top: r.bottom + 6, right });
  };
  return (
    <span tabIndex={0} onMouseEnter={show} onMouseLeave={() => setPos(null)} onFocus={show} onBlur={() => setPos(null)}
      style={{ cursor: "help", outline: "none", borderBottom: "1px dotted var(--line-max)" }}>
      {children}
      {pos && (
        <div role="tooltip" style={{ position: "fixed", ...pos, zIndex: "var(--z-dropdown)" as unknown as number, background: "var(--surface-card)", border: "1px solid var(--line-strong)", borderRadius: 10, boxShadow: "var(--shadow-pop)", padding: "10px 12px", textAlign: "left", whiteSpace: "nowrap", fontWeight: 400, letterSpacing: 0, textTransform: "none" }}>
          {content}
        </div>
      )}
    </span>
  );
}

// ─── Relatório pro cliente (PDF via impressão do navegador, padrão reports/conversations) ──
interface ReportLead { ref: string | null; canal: string; ia: boolean; motivo: string | null }
interface Report extends Row { month: string; leads: ReportLead[] }

// Nome do agente é editável pelo cliente no portal: tudo que vai pro HTML passa por esc().
const esc = (s: unknown) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
const maskRef = (ref: string | null) => !ref ? "sem identificação" : ref.startsWith("@") ? `@•••${ref.slice(1)}` : `••••${ref}`;
const countsLine = (c: Counts, canal = false) =>
  Object.entries(c).sort((a, b) => b[1] - a[1]).map(([k, v]) => `${canal ? (CANAL_LABEL[k] ?? k) : k} ${int.format(v)}`).join(" · ");

async function exportReport(agentId: string, month: string) {
  // Janela aberta já no clique (senão o bloqueador de popup barra depois do await).
  const w = window.open("", "_blank");
  if (!w) { toastError("Popup bloqueado pelo navegador."); return; }
  w.document.write("<p style='font-family:sans-serif;padding:24px;color:#666'>Gerando relatório…</p>");
  let r: Report;
  try {
    r = await api.get<Report>(`/finance/agents/${agentId}/report?month=${month}`);
  } catch (e) {
    w.close(); toastError(errMsg(e)); return;
  }
  const mes = monthLabel(month);
  const atendidos = r.leads.filter(l => l.ia), nao = r.leads.filter(l => !l.ia);
  const over = r.extra_leads > 0;
  const parcial = month === thisMonth() ? ` · parcial, mês em andamento (até ${new Date().toLocaleDateString("pt-BR")})` : "";
  const plano = r.lead_limit === null
    ? `<p class="muted">Sem limite contratado neste mês.</p>`
    : `<table class="plano">
        <tr><td>Limite do mês</td><td>${int.format(r.lead_limit)} leads</td></tr>
        <tr><td>Atendidos pela IA</td><td>${int.format(r.leads_ia)} leads</td></tr>
        <tr><td>Excedente</td><td>${over ? `+${int.format(r.extra_leads)} leads` : "nenhum"}</td></tr>
        <tr><td>Valor por lead extra</td><td>${r.extra_price === null ? "—" : brl.format(r.extra_price)}</td></tr>
        <tr class="total${over ? " over" : ""}"><td>Total adicional</td><td>${brl.format(r.extra_cost)}</td></tr>
      </table>`;
  // Um bloco por canal (maior primeiro), números embaralhados em grade de 5 colunas:
  // cliente com milhares de leads não vira 100 páginas de uma linha por número.
  const list = (items: ReportLead[]) => {
    if (!items.length) return `<p class="muted">Nenhum.</p>`;
    const byCanal = new Map<string, ReportLead[]>();
    for (const l of items) {
      const k = CANAL_LABEL[l.canal] ?? l.canal;
      const arr = byCanal.get(k);
      if (arr) arr.push(l); else byCanal.set(k, [l]);
    }
    return [...byCanal].sort((a, b) => b[1].length - a[1].length).map(([canal, ls]) => {
      const s = [...ls];
      for (let i = s.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [s[i], s[j]] = [s[j], s[i]]; }
      return `<h3>${esc(canal)} (${int.format(s.length)})</h3><div class="leads">${s.map(l => `<span>${esc(maskRef(l.ref))}</span>`).join("")}</div>`;
    }).join("");
  };

  w.document.open();
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Relatório ${esc(r.name)} — ${esc(mes)}</title>
    <style>
      @page { size: A4; margin: 14mm 12mm; }
      * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      body { font-family: Inter, -apple-system, "Segoe UI", Roboto, sans-serif; color: #18181D; font-size: 12px; margin: 0; }
      .wrap { max-width: 780px; margin: 0 auto; padding: 24px; }
      header { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding-bottom: 16px; border-bottom: 2px solid #C32020; }
      .plate { background: #18181D; border-radius: 8px; padding: 10px 14px; display: inline-flex; }
      .plate img { height: 22px; }
      h1 { font-size: 20px; margin: 0; letter-spacing: -.01em; } .sub { color: #71717A; margin-top: 3px; }
      h2 { font-size: 10.5px; text-transform: uppercase; letter-spacing: .06em; color: #71717A; margin: 22px 0 8px; }
      .kpis { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
      .kpi { border: 1px solid #E6E6EA; border-radius: 10px; padding: 12px 14px; break-inside: avoid; }
      .kpi .l { font-size: 10px; text-transform: uppercase; letter-spacing: .05em; color: #71717A; font-weight: 600; }
      .kpi .v { font-size: 22px; font-weight: 700; font-variant-numeric: tabular-nums; margin-top: 2px; }
      .kpi .d { font-size: 10.5px; color: #4B4B54; margin-top: 4px; line-height: 1.45; }
      table.plano { width: 100%; border-collapse: collapse; }
      .plano td { padding: 7px 0; border-bottom: 1px solid #EBEBEE; }
      .plano td:last-child { text-align: right; font-variant-numeric: tabular-nums; font-weight: 600; }
      .plano tr.total td { border-bottom: none; font-size: 14px; padding-top: 10px; }
      .plano tr.total.over td { color: #C32020; }
      h3 { font-size: 11px; font-weight: 600; margin: 12px 0 5px; color: #3A3A43; }
      .leads { display: grid; grid-template-columns: repeat(5, 1fr); gap: 1px 14px; font-size: 10px; font-variant-numeric: tabular-nums; color: #3A3A43; }
      .leads span { padding: 1px 0; white-space: nowrap; }
      .muted { color: #71717A; margin: 0; }
      footer { margin-top: 26px; padding-top: 10px; border-top: 1px solid #E6E6EA; color: #71717A; font-size: 9.5px; line-height: 1.5; }
    </style></head><body><div class="wrap">
      <header>
        <div><h1>${esc(r.name)}</h1><div class="sub">Relatório de atendimento · ${esc(mes)}${esc(parcial)}</div></div>
        <div class="plate"><img src="${location.origin}/logo-carmillion.avif" alt="Carmillion" onload="print()" onerror="print()"></div>
      </header>
      <h2>Resumo</h2>
      <div class="kpis">
        <div class="kpi"><div class="l">Atendidos pela IA</div><div class="v">${int.format(r.leads_ia)}</div><div class="d">${esc(countsLine(r.ia_por_canal, true))}</div></div>
        <div class="kpi"><div class="l">Não atendidos pela IA</div><div class="v">${int.format(r.leads_nao_ia)}</div><div class="d">${esc(countsLine(r.nao_por_motivo))}</div></div>
        <div class="kpi"><div class="l">Leads únicos</div><div class="v">${int.format(r.leads_unicos)}</div><div class="d">falaram com o atendimento no mês</div></div>
      </div>
      <h2>Plano</h2>${plano}
      <h2>Leads atendidos pela IA (${int.format(atendidos.length)})</h2>${list(atendidos)}
      <h2>Leads não atendidos pela IA (${int.format(nao.length)})</h2>${list(nao)}
      <footer>Gerado em ${esc(new Date().toLocaleString("pt-BR"))}. Cada lead conta uma vez no mês. Atendido pela IA = recebeu qualquer envio da IA (resposta, follow-up ou template de portal).
        Por privacidade, a identificação mostra só os 4 últimos dígitos do telefone; no Instagram e no Messenger, o final do @ ou do ID do perfil.</footer>
    </div></body></html>`);
  w.document.close();
}

// Cor do consumo do limite: verde até 80%, âmbar até 100%, vermelho estourado.
const usageColor = (ia: number, limit: number) => ia > limit ? "var(--danger)" : ia >= limit * 0.8 ? "var(--warn)" : "var(--ok)";

// ─── Página ─────────────────────────────────────────────────────────────────
function FinanceiroPage() {
  const [month, setMonth] = useState(thisMonth);
  const [search, setSearch] = useState("");
  const [onlyOver, setOnlyOver] = useState(false);
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: "leads_ia", dir: -1 });
  const [editing, setEditing] = useState<Row | null>(null);
  const { data: rows = [], isLoading, error } = useQuery<Row[]>({
    queryKey: ["finance-agents", month],
    queryFn: () => api.get(`/finance/agents?month=${month}`),
    placeholderData: (prev) => prev,
  });

  const over = rows.filter(r => r.extra_leads > 0);
  const totals = {
    ia: rows.reduce((s, r) => s + r.leads_ia, 0),
    nao: rows.reduce((s, r) => s + r.leads_nao_ia, 0),
    unicos: rows.reduce((s, r) => s + r.leads_unicos, 0),
    cobrar: over.reduce((s, r) => s + r.extra_cost, 0),
    semLimite: rows.filter(r => r.lead_limit === null).length,
  };

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = rows.filter(r => (!onlyOver || r.extra_leads > 0) && (!q || r.name.toLowerCase().includes(q) || (r.manager_name ?? "").toLowerCase().includes(q)));
    return [...list].sort((a, b) => {
      if (sort.key === "name") return a.name.localeCompare(b.name, "pt-BR") * sort.dir;
      const av = a[sort.key], bv = b[sort.key];
      if (av === null) return 1; // sem limite/valor sempre no fim, nos dois sentidos
      if (bv === null) return -1;
      return (av - bv) * sort.dir || a.name.localeCompare(b.name, "pt-BR");
    });
  }, [rows, search, onlyOver, sort]);

  const isCurrent = month === thisMonth();
  const th = (key: SortKey, label: string, right = true) => {
    const active = sort.key === key;
    return (
      <th style={{ ...thBase, textAlign: right ? "right" : "left" }} aria-sort={active ? (sort.dir === 1 ? "ascending" : "descending") : "none"}>
        <button
          onClick={() => setSort(active ? { key, dir: sort.dir === 1 ? -1 : 1 } : { key, dir: key === "name" ? 1 : -1 })}
          style={{ all: "unset", cursor: "pointer", color: active ? "var(--ink)" : "inherit", display: "inline-flex", alignItems: "center", gap: 4 }}
        >
          {label}<span style={{ opacity: active ? 1 : 0.3, fontSize: 10 }}>{active && sort.dir === 1 ? "▲" : "▼"}</span>
        </button>
      </th>
    );
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {editing && <EditModal row={editing} month={month} onClose={() => setEditing(null)} />}

      <div style={{ height: 52, borderBottom: "1px solid var(--line)", display: "flex", alignItems: "center", padding: "0 24px", flexShrink: 0, gap: 10 }}>
        <h1 style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)", margin: 0 }}>Financeiro</h1>
        <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>leads por agente no mês, limite e excedente a cobrar</span>
        <div style={{ flex: 1 }} />
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <button onClick={() => setMonth(shiftMonth(month, -1))} aria-label="Mês anterior" style={{ ...btnGhost, padding: "6px 10px" }}>‹</button>
          <input type="month" aria-label="Mês" value={month} max={thisMonth()} onChange={e => e.target.value && setMonth(e.target.value)} style={{ ...inputStyle, width: 160, padding: "6px 10px" }} />
          <button onClick={() => setMonth(shiftMonth(month, 1))} disabled={isCurrent} aria-label="Próximo mês" style={{ ...btnGhost, padding: "6px 10px", opacity: isCurrent ? 0.4 : 1 }}>›</button>
          {!isCurrent && <button onClick={() => setMonth(thisMonth())} style={{ ...btnGhost, padding: "6px 10px" }}>Mês atual</button>}
        </div>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: 24, display: "flex", flexDirection: "column", gap: 20 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
          <Kpi label="Atendidos pela IA" value={int.format(totals.ia)} sub={`em ${monthLabel(month)}`}
            popup={splitContent([{ title: "Por canal", data: sumCounts(rows.map(r => r.ia_por_canal)), canal: true }])} />
          <Kpi label="Não atendidos" value={int.format(totals.nao)} sub="falaram e a IA não respondeu"
            popup={splitContent([
              { title: "Por canal", data: sumCounts(rows.map(r => r.nao_por_canal)), canal: true },
              { title: "Por motivo", data: sumCounts(rows.map(r => r.nao_por_motivo)) },
            ])} />
          <Kpi label="Leads únicos" value={int.format(totals.unicos)} sub="falaram com algum agente" />
          <Kpi label="Acima do limite" value={int.format(over.length)} sub={totals.semLimite ? `${totals.semLimite} agente${totals.semLimite > 1 ? "s" : ""} sem limite cadastrado` : "de todos os agentes"} alert={over.length > 0} />
          <Kpi label="Excedente a cobrar" value={brl.format(totals.cobrar)} sub={isCurrent ? "parcial: o mês ainda está correndo" : "fechamento do mês"} alert={totals.cobrar > 0} />
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input style={{ ...inputStyle, flex: "0 1 340px", background: "var(--surface-hover)" }} placeholder="Buscar agente ou gestor..." value={search} onChange={e => setSearch(e.target.value)} aria-label="Buscar agente" />
          <button
            onClick={() => setOnlyOver(!onlyOver)}
            aria-pressed={onlyOver}
            style={{ ...btnGhost, display: "inline-flex", alignItems: "center", gap: 6, ...(onlyOver ? { background: "var(--danger-soft)", color: "var(--danger)", borderColor: "var(--danger-soft-line)" } : {}) }}
          >
            <AlertTriangleIcon size={13} />Só acima do limite{over.length ? ` (${over.length})` : ""}
          </button>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>{shown.length} de {rows.length} agentes</span>
        </div>

        {error && <span style={{ fontSize: 13, color: "var(--danger)" }}>{errMsg(error)}</span>}
        <div style={{ border: "1px solid var(--line)", borderRadius: 12, overflow: "auto", background: "var(--surface-card)", opacity: isLoading ? 0.6 : 1, transition: "opacity .15s" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...thBase, width: 36, textAlign: "right" }}>#</th>
                {th("name", "Agente", false)}
                {th("leads_unicos", "Leads únicos")}
                {th("leads_ia", "Atendidos IA")}
                {th("leads_nao_ia", "Não atendidos")}
                {th("lead_limit", "Limite/mês")}
                {th("extra_price", "Valor lead extra")}
                {th("extra_leads", "Excedente")}
                {th("extra_cost", "A cobrar")}
                <th style={{ ...thBase, width: 84 }} aria-label="Ações" />
              </tr>
            </thead>
            <tbody>
              {isLoading && rows.length === 0 && <tr><td style={{ ...tdStyle, color: "var(--ink-muted)" }} colSpan={10}>Carregando…</td></tr>}
              {!isLoading && shown.length === 0 && <tr><td style={{ ...tdStyle, color: "var(--ink-muted)" }} colSpan={10}>{rows.length ? "Nenhum agente encontrado." : "Nenhum agente."}</td></tr>}
              {shown.map((r, i) => {
                const isOver = r.extra_leads > 0;
                const pct = r.lead_limit ? Math.min(1, r.leads_ia / r.lead_limit) : 0;
                return (
                  <tr key={r.agent_id} style={{ background: isOver ? "var(--danger-soft)" : undefined }}
                    onMouseOver={e => { if (!isOver) e.currentTarget.style.background = "var(--surface-hover)"; }}
                    onMouseOut={e => { if (!isOver) e.currentTarget.style.background = ""; }}>
                    <td style={{ ...numCell, color: "var(--ink-muted)", fontSize: 12, boxShadow: isOver ? "inset 3px 0 0 var(--danger)" : undefined }}>{i + 1}</td>
                    <td style={{ ...tdStyle, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis" }}>
                      <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{r.name}</span>
                        {STATUS_LABEL[r.status] && <span style={{ fontSize: 11, fontWeight: 500, color: "var(--ink-muted)", background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 999, padding: "1px 8px" }}>{STATUS_LABEL[r.status]}</span>}
                      </div>
                      <div style={{ fontSize: 12, color: "var(--ink-muted)", marginTop: 2 }}>{r.manager_name ?? "sem gestor"}</div>
                    </td>
                    <td style={numCell}>{int.format(r.leads_unicos)}</td>
                    <td style={{ ...numCell, minWidth: 140 }}>
                      <div style={{ fontWeight: 600 }}>
                        <Hover content={splitContent([{ title: "Por canal", data: r.ia_por_canal, canal: true }])}>{int.format(r.leads_ia)}</Hover>
                        {r.lead_limit !== null && <span style={{ fontWeight: 400, color: "var(--ink-muted)" }}> / {int.format(r.lead_limit)}</span>}
                      </div>
                      {r.lead_limit !== null && (
                        <div style={{ height: 4, background: "var(--surface-hover)", borderRadius: 999, marginTop: 6, overflow: "hidden" }} title={r.lead_limit ? `${Math.round((r.leads_ia / r.lead_limit) * 100)}% do limite` : undefined}>
                          <div style={{ width: `${(r.lead_limit ? pct : 1) * 100}%`, height: "100%", background: usageColor(r.leads_ia, r.lead_limit), borderRadius: 999 }} />
                        </div>
                      )}
                    </td>
                    <td style={{ ...numCell, color: r.leads_nao_ia ? "var(--warn)" : "var(--ink-dim)", fontWeight: r.leads_nao_ia ? 600 : 400 }}>
                      {r.leads_nao_ia ? (
                        <Hover content={splitContent([
                          { title: "Por canal", data: r.nao_por_canal, canal: true },
                          { title: "Por motivo", data: r.nao_por_motivo },
                        ])}>{int.format(r.leads_nao_ia)}</Hover>
                      ) : "—"}
                    </td>
                    <td style={numCell}>
                      {r.lead_limit === null ? <span style={{ color: "var(--ink-dim)" }}>—</span> : int.format(r.lead_limit)}
                      {r.ajustado && <span title={`Valores ajustados só para ${monthLabel(month)}`} style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--info)", background: "var(--info-soft)", border: "1px solid var(--info-line)", borderRadius: 4, padding: "1px 5px", verticalAlign: 1 }}>ajustado</span>}
                    </td>
                    <td style={numCell}>{r.extra_price === null ? <span style={{ color: "var(--ink-dim)" }}>—</span> : brl.format(r.extra_price)}</td>
                    <td style={{ ...numCell, color: isOver ? "var(--danger)" : "var(--ink-dim)", fontWeight: isOver ? 700 : 400 }}>{isOver ? `+${int.format(r.extra_leads)}` : "—"}</td>
                    <td style={{ ...numCell, color: isOver ? "var(--danger)" : "var(--ink-dim)", fontWeight: isOver ? 700 : 400 }}>{isOver ? brl.format(r.extra_cost) : "—"}</td>
                    <td style={{ ...tdStyle, padding: "6px 8px" }}>
                      <div style={{ display: "flex", gap: 2, justifyContent: "center" }}>
                        <IconBtn label={`Relatório PDF de ${r.name}`} title="Relatório PDF pro cliente" onClick={() => exportReport(r.agent_id, month)}><DocIcon /></IconBtn>
                        <IconBtn label={`Editar limite de ${r.name}`} title="Editar limite e valor" onClick={() => setEditing(r)}><PencilIcon /></IconBtn>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: 12, color: "var(--ink-muted)", lineHeight: 1.6 }}>
          <b>Atendidos IA</b>: leads que receberam qualquer envio da IA no mês (resposta, follow-up ou template de portal), cada lead conta uma vez por mês.{" "}
          <b>Não atendidos</b>: falaram e não receberam nada da IA no mês (passe o mouse pra ver canal e motivo).{" "}
          <b>Leads únicos</b>: leads que mandaram mensagem ou chegaram por portal. Números atualizados de hora em hora.
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, alert, popup }: { label: string; value: string; sub: string; alert?: boolean; popup?: React.ReactNode }) {
  return (
    <div style={{ ...card, padding: "14px 16px", ...(alert ? { background: "var(--danger-soft)", borderColor: "var(--danger-soft-line)" } : {}) }}>
      <div style={{ fontSize: 11, color: alert ? "var(--danger)" : "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em", fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4, letterSpacing: "-.01em", fontVariantNumeric: "tabular-nums", color: alert ? "var(--danger)" : "var(--ink)" }}>{popup ? <Hover content={popup}>{value}</Hover> : value}</div>
      <div style={{ fontSize: 12, color: "var(--ink-muted)", marginTop: 2 }}>{sub}</div>
    </div>
  );
}

function IconBtn({ label, title, onClick, children }: { label: string; title: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} aria-label={label} title={title}
      style={{ background: "none", border: "1px solid transparent", borderRadius: 6, cursor: "pointer", color: "var(--ink-muted)", padding: 6, display: "grid", placeItems: "center" }}
      onMouseOver={e => { e.currentTarget.style.borderColor = "var(--line-strong)"; e.currentTarget.style.color = "var(--ink)"; }}
      onMouseOut={e => { e.currentTarget.style.borderColor = "transparent"; e.currentTarget.style.color = "var(--ink-muted)"; }}>
      {children}
    </button>
  );
}

function DocIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5M9 13h6M9 17h4" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
    </svg>
  );
}

// ─── Editar padrão + mês ────────────────────────────────────────────────────
const toStr = (v: number | null, money = false) => v === null ? "" : money ? v.toFixed(2).replace(".", ",") : String(v);
// "1.234,50" / "1234.5" / "2,5" → número; vazio → null; inválido → NaN
const parseNum = (s: string, money = false): number | null => {
  const t = s.trim();
  if (!t) return null;
  const n = Number(money ? (t.includes(",") ? t.replace(/\./g, "").replace(",", ".") : t) : t);
  return Number.isFinite(n) && n >= 0 && (money || Number.isInteger(n)) ? n : NaN;
};

function EditModal({ row, month, onClose }: { row: Row; month: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    dLimit: toStr(row.default.lead_limit), dPrice: toStr(row.default.extra_price, true),
    mLimit: toStr(row.month_override.lead_limit), mPrice: toStr(row.month_override.extra_price, true),
  });
  const p = { dLimit: parseNum(f.dLimit), dPrice: parseNum(f.dPrice, true), mLimit: parseNum(f.mLimit), mPrice: parseNum(f.mPrice, true) };
  const invalid = Object.values(p).some(v => v !== null && Number.isNaN(v));
  const limit = p.mLimit ?? p.dLimit, price = p.mPrice ?? p.dPrice;
  const extra = limit !== null && !Number.isNaN(limit) ? Math.max(0, row.leads_ia - limit) : 0;
  const hasMonth = !!(f.mLimit.trim() || f.mPrice.trim());
  const mes = monthLabel(month);

  const save = useMutation({
    mutationFn: () => api.put(`/finance/agents/${row.agent_id}`, {
      month,
      default: { lead_limit: p.dLimit, extra_price: p.dPrice },
      month_override: { lead_limit: p.mLimit, extra_price: p.mPrice },
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["finance-agents"] }); onClose(); },
    onError: (e) => toastError(errMsg(e)),
  });
  const submit = () => { if (!invalid && !save.isPending) save.mutate(); };
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });
  const money = (id: string, k: "dPrice" | "mPrice", placeholder: string) => (
    <div style={{ position: "relative" }}>
      <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "var(--ink-muted)", pointerEvents: "none" }}>R$</span>
      <input id={id} inputMode="decimal" style={{ ...inputStyle, paddingLeft: 34, borderColor: Number.isNaN(p[k]) ? "var(--danger)" : undefined }} value={f[k]} onChange={set(k)} placeholder={placeholder} onKeyDown={e => e.key === "Enter" && submit()} />
    </div>
  );
  const limitInput = (id: string, k: "dLimit" | "mLimit", placeholder: string, autoFocus = false) => (
    <input id={id} autoFocus={autoFocus} inputMode="numeric" style={{ ...inputStyle, borderColor: Number.isNaN(p[k]) ? "var(--danger)" : undefined }} value={f[k]} onChange={set(k)} placeholder={placeholder} onKeyDown={e => e.key === "Enter" && submit()} />
  );

  return (
    <div {...backdropClose(onClose)} style={{ position: "fixed", inset: 0, background: "var(--overlay)", zIndex: "var(--z-modal)" as unknown as number, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <DialogPanel onClose={onClose} label={`Limite de ${row.name}`} style={{ background: "var(--surface-card)", border: "1px solid var(--line-strong)", borderRadius: 14, width: 480, maxWidth: "calc(100vw - 32px)", boxShadow: "var(--shadow-modal)", animation: "panel-in .18s var(--ease-out-quart)", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "18px 20px 14px", borderBottom: "1px solid var(--line)" }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.name}</div>
            <div style={{ fontSize: 12, color: "var(--ink-muted)" }}>{int.format(row.leads_ia)} atendidos pela IA em {mes}</div>
          </div>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} aria-label="Fechar" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink-muted)", padding: 4 }}><XIcon size={16} /></button>
        </div>

        <div style={{ padding: 20, display: "grid", gap: 18 }}>
          <section style={{ display: "grid", gap: 10 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>Padrão</div>
              <div style={{ fontSize: 12, color: "var(--ink-muted)" }}>Vale para todos os meses sem ajuste.</div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div><label htmlFor="d-limit" style={labelStyle}>Limite de leads/mês</label>{limitInput("d-limit", "dLimit", "sem limite", true)}</div>
              <div><label htmlFor="d-price" style={labelStyle}>Valor por lead extra</label>{money("d-price", "dPrice", "0,00")}</div>
            </div>
          </section>

          <section style={{ display: "grid", gap: 10, padding: 14, borderRadius: 10, background: hasMonth ? "var(--info-soft)" : "var(--surface-raised)", border: `1px solid ${hasMonth ? "var(--info-line)" : "var(--line)"}`, transition: "background .15s, border-color .15s" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>Só {mes}</div>
                <div style={{ fontSize: 12, color: "var(--ink-muted)" }}>Opcional. Campo vazio segue o padrão.</div>
              </div>
              <div style={{ flex: 1 }} />
              {hasMonth && <button onClick={() => setF({ ...f, mLimit: "", mPrice: "" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "var(--info)", padding: 0 }}>Voltar ao padrão</button>}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div><label htmlFor="m-limit" style={labelStyle}>Limite de leads/mês</label>{limitInput("m-limit", "mLimit", f.dLimit ? `${f.dLimit} (padrão)` : "padrão")}</div>
              <div><label htmlFor="m-price" style={labelStyle}>Valor por lead extra</label>{money("m-price", "mPrice", f.dPrice ? `${f.dPrice} (padrão)` : "padrão")}</div>
            </div>
          </section>

          <div style={{ fontSize: 13, padding: "10px 12px", borderRadius: 8, background: extra > 0 ? "var(--danger-soft)" : "var(--surface-hover)", color: extra > 0 ? "var(--danger)" : "var(--ink-secondary)", fontVariantNumeric: "tabular-nums" }}>
            {invalid ? "Confira os valores: só números positivos (limite inteiro)."
              : limit === null ? `Sem limite em ${mes}: nada a cobrar.`
              : extra > 0 ? <>Em {mes}: <b>+{int.format(extra)}</b> leads acima do limite · <b>{brl.format(extra * (price ?? 0))}</b> a cobrar</>
              : `Em ${mes}: dentro do limite (${int.format(row.leads_ia)} de ${int.format(limit)}).`}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "14px 20px", borderTop: "1px solid var(--line)" }}>
          <button onClick={onClose} style={btnGhost}>Cancelar</button>
          <button onClick={submit} disabled={invalid || save.isPending} style={{ ...btnPrimary, opacity: invalid || save.isPending ? 0.6 : 1 }}>{save.isPending ? "Salvando…" : "Salvar"}</button>
        </div>
      </DialogPanel>
    </div>
  );
}
